import classNames from 'classnames'
import React from 'react'

import { exists } from '~/utils'

import styles from './Typography.css'

type Color = 'error' | 'inherit' | 'light' | 'lighter' | 'link' | 'normal'
type FontSize = 10 | 12 | 14 | 16 | 24
type FontWeight = 400 | 700

type Variant =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'link1'
  | 'link2'
  | 'link3'
  | 'normal'
  | 'small'
  | 'subscript'

const variantColorMap: Record<Variant, Color> = {
  heading1: 'normal',
  heading2: 'normal',
  heading3: 'normal',
  normal: 'normal',
  small: 'normal',
  subscript: 'light',
  link1: 'link',
  link2: 'link',
  link3: 'link',
}
const variantFontSizeMap: Record<Variant, FontSize> = {
  heading1: 24,
  heading2: 16,
  heading3: 14,
  normal: 12,
  small: 10,
  subscript: 10,
  link1: 16,
  link2: 14,
  link3: 12,
}

const variantFontWeightMap: Record<Variant, FontWeight> = {
  heading1: 700,
  heading2: 700,
  heading3: 700,
  normal: 400,
  small: 400,
  subscript: 400,
  link1: 400,
  link2: 400,
  link3: 400,
}

export type TypographyProps = {
  children?: React.ReactNode
  className?: string
  color?: Color
  'data-testid'?: string
  fontSize?: FontSize
  fontWeight?: FontWeight
  lineHeight?: 2.5
  textAlign?: 'center' | 'end' | 'start'
  variant: Variant
  whiteSpace?: 'noWrap' | 'preWrap'
}

export const Typography = React.memo(
  ({
    children,
    className,
    color,
    'data-testid': dataTestId = 'components-elements-typography',
    fontSize,
    fontWeight,
    lineHeight,
    textAlign,
    variant,
    whiteSpace,
  }: TypographyProps) => (
    <span
      className={classNames(
        styles[variant],
        styles[`color-${color ?? variantColorMap[variant]}`],
        styles[`fontSize-${fontSize ?? variantFontSizeMap[variant]}`],
        styles[`fontWeight-${fontWeight ?? variantFontWeightMap[variant]}`],
        {
          [styles[`lineHeight-${lineHeight}`.replace(/\./g, '_')]]:
            exists(lineHeight),
          [styles[`textAlign-${textAlign}`]]: exists(textAlign),
          [styles[`whiteSpace-${whiteSpace}`]]: exists(whiteSpace),
        },
        className,
      )}
      data-testid={dataTestId}
    >
      {children}
    </span>
  ),
)
